import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import {
  useLazyGetAdvisorSessionsQuery,
  useLazyFetchSubscriptionDetailsQuery
} from "../../redux/queries"
import { DateRangePreset } from "../ui/activity/date-filter"
import { AdvisorSession, SubscriptionDetails } from "../../types"

const LIMIT = 20

const presetDays: Partial<Record<DateRangePreset, number>> = {
  today: 0,
  last_7: 7,
  last_30: 30,
  last_90: 90
}

const getDateRange = (
  preset: DateRangePreset,
  customStart: number | null,
  customEnd: number | null
): { min?: number; max?: number } => {
  if (preset === "custom" && customStart && customEnd) {
    return { min: customStart, max: customEnd }
  }
  const days = presetDays[preset]
  if (days == null) return {}
  return {
    min: DateTime.now().minus({ days }).startOf("day").toMillis()
  }
}

export const useAdvisorConversations = () => {
  const [fetchPage, { isFetching }] = useLazyGetAdvisorSessionsQuery()

  const [allSessions, setAllSessions] = useState<AdvisorSession[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [triggerSub] = useLazyFetchSubscriptionDetailsQuery()
  const fetchedIds = useRef(new Set<string>())
  const isMounted = useRef(false)
  const [subscriptionMap, setSubscriptionMap] = useState<
    Record<string, SubscriptionDetails>
  >({})

  const fetchSubscriptions = useCallback(
    (sessions: AdvisorSession[]) => {
      const newIds = sessions
        .map(s => s.subscriptionId)
        .filter(id => id && !fetchedIds.current.has(id))

      if (!newIds.length) return

      newIds.forEach(id => fetchedIds.current.add(id))
      Promise.all(
        newIds.map(id =>
          triggerSub({ id })
            .unwrap()
            .catch(() => null)
        )
      ).then(results => {
        const updates: Record<string, SubscriptionDetails> = {}
        results.forEach((data, i) => {
          if (data) updates[newIds[i]] = data
        })
        if (Object.keys(updates).length) {
          setSubscriptionMap(prev => ({ ...prev, ...updates }))
        }
      })
    },
    [triggerSub]
  )

  const fetchAndAppend = useCallback(
    async (pageNum: number, reset = false) => {
      try {
        const result = await fetchPage({ page: pageNum, limit: LIMIT }).unwrap()
        const incoming = result.conversations ?? []
        setAllSessions(prev => (reset ? incoming : [...prev, ...incoming]))
        setHasMore(pageNum < result.totalPages)
        setPage(pageNum + 1)
        fetchSubscriptions(incoming)
      } catch {
        // handled by RTK Query
      } finally {
        setIsLoading(false)
      }
    },
    [fetchPage, fetchSubscriptions]
  )

  // Initial load
  useEffect(() => {
    setIsLoading(true)
    fetchAndAppend(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMore = useCallback(() => {
    if (!hasMore || isFetching) return
    fetchAndAppend(page)
  }, [hasMore, isFetching, page, fetchAndAppend])

  const [searchQuery, setSearchQuery] = useState("")
  const [selectedBrandId, setSelectedBrandId] = useState("")
  const [datePreset, setDatePreset] = useState<DateRangePreset>("all")
  const [customDateStart, setCustomDateStart] = useState<number | null>(null)
  const [customDateEnd, setCustomDateEnd] = useState<number | null>(null)

  // Reset + refetch when brand filter changes (skip initial mount)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    setIsLoading(true)
    setAllSessions([])
    setPage(1)
    setHasMore(false)
    fetchAndAppend(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId])

  const handleDateChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      if (range === "custom" && startDate && endDate) {
        setDatePreset("custom")
        setCustomDateStart(startDate.getTime())
        setCustomDateEnd(endDate.getTime())
      } else {
        setDatePreset(range)
        setCustomDateStart(null)
        setCustomDateEnd(null)
      }
    },
    []
  )

  const handleClearDate = useCallback(() => {
    setDatePreset("all")
    setCustomDateStart(null)
    setCustomDateEnd(null)
  }, [])

  const brandOptions = useMemo(() => {
    if (!allSessions.length) return []
    const ids = new Set(allSessions.map(s => s.brandId).filter(Boolean))
    return Array.from(ids)
  }, [allSessions])

  const sessions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const { min, max } = getDateRange(
      datePreset,
      customDateStart,
      customDateEnd
    )

    return allSessions.filter(s => {
      if (q) {
        const sub = subscriptionMap[s.subscriptionId]
        if (!sub) return false
        const fullName = `${sub.first_name ?? ""} ${
          sub.last_name ?? ""
        }`.toLowerCase()
        if (
          !fullName.includes(q) &&
          !(sub.email ?? "").toLowerCase().includes(q)
        )
          return false
      }

      if (selectedBrandId && s.brandId !== selectedBrandId) return false

      if (min || max) {
        const t = new Date(s.lastActivityAt).getTime()
        if (min && t < min) return false
        if (max && t > max) return false
      }

      return true
    })
  }, [
    allSessions,
    searchQuery,
    selectedBrandId,
    datePreset,
    customDateStart,
    customDateEnd,
    subscriptionMap
  ])

  return {
    sessions,
    isLoading,
    hasMore,
    isFetchingMore: isFetching,
    loadMore,
    searchQuery,
    setSearchQuery,
    selectedBrandId,
    setSelectedBrandId,
    brandOptions,
    datePreset,
    customDateStart,
    customDateEnd,
    handleDateChange,
    handleClearDate,
    subscriptionMap
  }
}
